// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { OptionsSelector } from '@shared/ui-kit/components/options-selector';
import { type FC, useCallback, useMemo } from 'react';
import { type Tag } from '@shared/data-access/interfaces/redux/tag-manager-state';
import { TagItem } from './tag-item';
import { AssetBrand } from '@shared/ui-kit/components/popups';
import { WatchlistToggleButton } from '@shared/ui-kit/components/table';
import { useInvestmentPopup } from '../investment-popup';
import { useTranslation } from 'react-i18next';

export interface TagSegmentProps {
  name: string;
  ticker: string;
  isin?: string | null;
  logo?: string | null;
  assetType?: string;
  assetClass?: string;
  options: Tag[];
  selectedTags: string[];
  onTickerTag: (tickers: string[], tag: string) => void;
  onTickerTagDelete: (tag: string, ticker: string) => void;
}

export const TagSegment: FC<TagSegmentProps> = ({
  ticker,
  logo,
  name,
  assetClass,
  isin,
  assetType,
  options,
  selectedTags,
  onTickerTag,
  onTickerTagDelete,
}: TagSegmentProps) => {
  const { openInvestmentPopup } = useInvestmentPopup();
  const { t } = useTranslation();

  const optionValues = useMemo(() => options.map(option => option.name), [options]);

  const onChangeValueSelected = useCallback(
    (value: string) => {
      if (!selectedTags.includes(value)) {
        onTickerTag([ticker], value);
      }
    },
    [onTickerTag, selectedTags, ticker],
  );

  return (
    <>
      <div className="col-span-1 mb-2.5 flex items-baseline rounded-lg p-2 text-border-dark">
        <AssetBrand
          ticker={ticker}
          assetType={assetType}
          logoUrl={logo!}
          label={name}
          analyzePopup={openInvestmentPopup}
          watchlistButton={
            <WatchlistToggleButton ticker={ticker} assetType={assetType!} />
          }
        />
      </div>

      <div className="col-span-1 mb-2.5 flex items-baseline rounded-lg p-2 text-border-dark">
        <p>{ticker ?? isin}</p>
      </div>

      <div className="col-span-1 mb-2.5 flex items-baseline rounded-lg p-2 text-border-dark">
        <p>{assetClass}</p>
      </div>

      <div className="assign-tag-selector col-span-1 mb-2.5 flex items-baseline p-2">
        <OptionsSelector
          optionsAvailable={optionValues}
          onChangeValueSelected={onChangeValueSelected}
          optionSelected={''}
          defaultText={t('tag-manager.ticker-tags', { ns: 'components' })}
          selectType="white"
        />
      </div>

      <div className="col-span-1 flex items-start overflow-scroll rounded-lg p-2">
        <div className="grid grid-cols-2 gap-6">
          {Array.from(selectedTags, (tag, key) => (
            <TagItem
              key={`${key}-${tag}`}
              label={tag}
              tag={tag}
              ticker={ticker}
              data-testid={`${key}-${tag}`}
              onDelete={onTickerTagDelete}
            />
          ))}
        </div>
      </div>
    </>
  );
};
